import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useGetAccountReporting } from '../gql/queries/useGetAccountReporting';
import { useGetRefundsByAccount } from '../gql/queries/useGetRefundsByAccount';

export default function PromoterDashboard() {
  const [selectedPeriod, setSelectedPeriod] = useState('Last 30 Days');
  const params = useParams();

  const { data } = useGetAccountReporting(parseInt(params.id));
  const { data: refundsData, loading: refundsLoading } = useGetRefundsByAccount(
    { accountId: parseInt(params.id) }
  );

  const promoterData = {
    name: data && data.getAccount ? data.getAccount.business_dba : '',
    totalProfit: 4287.5,
    appProfit: 2847.25,
    posProfit: 1440.25,
    eventCount: 12,
    stripeBalance: data && data.getAccount.stripe_balance,
    payoutBalance: data && data.getAccount.payout_balances.total,
  };

  const events = [
    {
      name: 'Friday Night Thunder',
      date: 'Jul 15, 2025',
      type: 'Dirt Track',
      appSales: 5200,
      posSales: 3350,
      appProfit: 251.0,
      posProfit: 161.35,
      totalProfit: 412.35,
    },
    {
      name: 'Saturday Championship',
      date: 'Jul 12, 2025',
      type: 'Asphalt',
      appSales: 8925,
      posSales: 5250,
      appProfit: 430.61,
      posProfit: 251.83,
      totalProfit: 682.44,
    },
    {
      name: 'Late Model Special',
      date: 'Jul 8, 2025',
      type: 'Dirt Track',
      appSales: 4650,
      posSales: 2800,
      appProfit: 224.43,
      posProfit: 135.33,
      totalProfit: 359.76,
    },
  ];

  const ticketTypes = [
    {
      type: 'General Admission',
      ticketsSold: 1847,
      appSales: 28450,
      posSales: 17825,
      profit: 2234.85,
      avgProfit: 1.21,
    },
    {
      type: 'VIP/Premium',
      ticketsSold: 287,
      appSales: 8925,
      posSales: 5750,
      profit: 1147.3,
      avgProfit: 4.0,
    },
    {
      type: 'Parking',
      ticketsSold: 542,
      appSales: 3250,
      posSales: 2175,
      profit: 294.45,
      avgProfit: 0.54,
    },
  ];

  const membershipTypes = [
    {
      type: 'Season Pass',
      activeMemberships: 342,
      newThisPeriod: 28,
      renewalsThisPeriod: 314,
      appSales: 15600,
      posSales: 8200,
      profit: 1247.5,
      avgProfit: 3.65,
    },
    {
      type: 'VIP Annual',
      activeMemberships: 89,
      newThisPeriod: 12,
      renewalsThisPeriod: 77,
      appSales: 8900,
      posSales: 3200,
      profit: 632.8,
      avgProfit: 7.11,
    },
    {
      type: 'Pit Crew Membership',
      activeMemberships: 156,
      newThisPeriod: 19,
      renewalsThisPeriod: 137,
      appSales: 4680,
      posSales: 2340,
      profit: 365.45,
      avgProfit: 2.34,
    },
    {
      type: 'Junior Racing Club',
      activeMemberships: 67,
      newThisPeriod: 8,
      renewalsThisPeriod: 59,
      appSales: 1340,
      posSales: 670,
      profit: 105.2,
      avgProfit: 1.57,
    },
  ];

  // Process refunds data from API
  const recentRefunds =
    refundsData?.getRefundsByAccount?.results?.map((refund) => ({
      id: refund.id,
      purchaserOrderNumber: refund.purchase.id,
      customerName: refund.user ? refund.user.formatted_name : '',
      eventName: refund?.purchase?.event?.name, // Keep hard coded as requested
      refundTotal: refund.total,
      trackAmount: refund.track_amount,
      feeImpact: -Math.abs(refund.fees), // Make sure it's negative
      refundDate: new Date(refund.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
      reason: 'Customer Request', // Keep hard coded as requested
      status: 'Completed', // Keep hard coded as requested
    })) || [];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return { backgroundColor: '#d1fae5', color: '#065f46' };
      case 'Processing':
        return { backgroundColor: '#fef3c7', color: '#92400e' };
      case 'Failed':
        return { backgroundColor: '#fee2e2', color: '#991b1b' };
      default:
        return { backgroundColor: '#f3f4f6', color: '#374151' };
    }
  };

  const headerStyle = {
    background: 'linear-gradient(135deg, #000033 0%, #fa4616 100%)',
    color: 'white',
    padding: '2rem',
    borderRadius: '12px',
    marginBottom: '2rem',
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '2rem',
    marginBottom: '2rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0',
  };

  const metricCardStyle = {
    backgroundColor: '#f8fafc',
    padding: '1.5rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    textAlign: 'center',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  };

  const thStyle = {
    padding: '12px',
    textAlign: 'left',
    backgroundColor: '#f8fafc',
    fontWeight: '600',
    color: '#374151',
    borderBottom: '1px solid #e2e8f0',
  };

  const tdStyle = {
    padding: '12px',
    borderBottom: '1px solid #e2e8f0',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        padding: '2rem',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* Header */}
      <div style={headerStyle}>
        <h1
          style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            margin: '0 0 0.5rem 0',
          }}
        >
          Promoter Profitability Dashboard
        </h1>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Track platform revenue by promoter account
        </p>
      </div>

      {/* Account Info */}
      <div style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
            {promoterData.name}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label style={{ fontWeight: '500' }} htmlFor="timePeriod">
              Time Period:
            </label>
            <select
              id="timePeriod"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
              }}
            >
              <option>Last 30 Days</option>
              <option>Last 90 Days</option>
              <option>Year to Date</option>
              <option>Custom Range</option>
            </select>
          </div>
        </div>

        {/* Metrics Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
          }}
        >
          <div style={metricCardStyle}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#fa4616',
                marginBottom: '0.5rem',
              }}
            >
              {formatCurrency(promoterData.totalProfit)}
            </div>
            <div
              style={{
                fontSize: '0.875rem',
                color: '#000033',
                fontWeight: '500',
              }}
            >
              Total Platform Profit
            </div>
          </div>

          <div style={metricCardStyle}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#fa4616',
                marginBottom: '0.5rem',
              }}
            >
              {formatCurrency(promoterData.appProfit)}
            </div>
            <div
              style={{
                fontSize: '0.875rem',
                color: '#000033',
                fontWeight: '500',
              }}
            >
              App Profit
            </div>
          </div>

          <div style={metricCardStyle}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#fa4616',
                marginBottom: '0.5rem',
              }}
            >
              {formatCurrency(promoterData.posProfit)}
            </div>
            <div
              style={{
                fontSize: '0.875rem',
                color: '#000033',
                fontWeight: '500',
              }}
            >
              POS Profit
            </div>
          </div>

          <div style={metricCardStyle}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#fa4616',
                marginBottom: '0.5rem',
              }}
            >
              {promoterData.eventCount}
            </div>
            <div
              style={{
                fontSize: '0.875rem',
                color: '#000033',
                fontWeight: '500',
              }}
            >
              Events This Period
            </div>
          </div>

          <div style={metricCardStyle}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#fa4616',
                marginBottom: '0.5rem',
              }}
            >
              {promoterData.stripeBalance}
            </div>
            <div
              style={{
                fontSize: '0.875rem',
                color: '#000033',
                fontWeight: '500',
              }}
            >
              Stripe Balance
            </div>
          </div>

          <div style={metricCardStyle}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#fa4616',
                marginBottom: '0.5rem',
              }}
            >
              {promoterData.payoutBalance}
            </div>
            <div
              style={{
                fontSize: '0.875rem',
                color: '#000033',
                fontWeight: '500',
              }}
            >
              Payout Balance
            </div>
          </div>
        </div>
      </div>

      {/* Event Performance Table */}
      <div style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
            Event Performance
          </h3>
          <button
            onClick={() => {
              console.warn('Clicking Click');
            }}
            style={{
              backgroundColor: '#fa4616',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Export to CSV
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Event Name</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>App Sales</th>
                <th style={thStyle}>POS Sales</th>
                <th style={thStyle}>App Profit</th>
                <th style={thStyle}>POS Profit</th>
                <th style={thStyle}>Total Profit</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event, index) => (
                <tr key={index}>
                  <td style={tdStyle}>{event.name}</td>
                  <td style={tdStyle}>{event.date}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor:
                          event.type === 'Dirt Track' ? '#fff5f2' : '#f0f0ff',
                        color:
                          event.type === 'Dirt Track' ? '#fa4616' : '#000033',
                      }}
                    >
                      {event.type}
                    </span>
                  </td>
                  <td style={tdStyle}>{formatCurrency(event.appSales)}</td>
                  <td style={tdStyle}>{formatCurrency(event.posSales)}</td>
                  <td
                    style={{ ...tdStyle, fontWeight: '600', color: '#fa4616' }}
                  >
                    {formatCurrency(event.appProfit)}
                  </td>
                  <td
                    style={{ ...tdStyle, fontWeight: '600', color: '#fa4616' }}
                  >
                    {formatCurrency(event.posProfit)}
                  </td>
                  <td
                    style={{ ...tdStyle, fontWeight: '600', color: '#fa4616' }}
                  >
                    {formatCurrency(event.totalProfit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Refunds Section */}
      <div style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
            Recent Refunds
          </h3>
          <Link
            to={`/admin/accounts/refunds/${params.id}`}
            style={{
              backgroundColor: '#fa4616',
              color: 'white',
              textDecoration: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            View All Refunds
          </Link>
        </div>

        <div style={{ overflowX: 'auto' }}>
          {refundsLoading ? (
            <div
              style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}
            >
              Loading refunds...
            </div>
          ) : recentRefunds.length === 0 ? (
            <div
              style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}
            >
              No refunds found for this account.
            </div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Order #</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Event</th>
                  <th style={thStyle}>Refund Total</th>
                  <th style={thStyle}>Track Amount</th>
                  <th style={thStyle}>Fee Impact</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Reason</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentRefunds.map((refund) => (
                  <tr key={refund.id}>
                    <td
                      style={{
                        ...tdStyle,
                        fontWeight: '600',
                        color: '#fa4616',
                      }}
                    >
                      <Link
                        to={`/admin/transactions/detail/${refund.purchaserOrderNumber}`}
                        style={{
                          color: '#fa4616',
                          textDecoration: 'none',
                          fontWeight: '600',
                        }}
                        onMouseOver={(e) =>
                          (e.target.style.textDecoration = 'underline')
                        }
                        onMouseOut={(e) =>
                          (e.target.style.textDecoration = 'none')
                        }
                      >
                        {refund.purchaserOrderNumber}
                      </Link>
                    </td>
                    <td style={tdStyle}>{refund.customerName}</td>
                    <td style={tdStyle}>{refund.eventName}</td>
                    <td style={tdStyle}>
                      {formatCurrency(refund.refundTotal)}
                    </td>
                    <td style={tdStyle}>
                      {formatCurrency(refund.trackAmount)}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        fontWeight: '600',
                        color: '#dc2626',
                      }}
                    >
                      {formatCurrency(refund.feeImpact)}
                    </td>
                    <td style={tdStyle}>{refund.refundDate}</td>
                    <td style={tdStyle}>{refund.reason}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          ...getStatusColor(refund.status),
                        }}
                      >
                        {refund.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Ticket Type Breakdown */}
      <div style={cardStyle}>
        <h3
          style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '1.5rem',
          }}
        >
          Ticket Type Breakdown
        </h3>

        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Ticket Type</th>
                <th style={thStyle}>Tickets Sold</th>
                <th style={thStyle}>App Sales</th>
                <th style={thStyle}>POS Sales</th>
                <th style={thStyle}>Our Profit</th>
                <th style={thStyle}>Avg Profit/Ticket</th>
              </tr>
            </thead>
            <tbody>
              {ticketTypes.map((ticket, index) => (
                <tr key={index}>
                  <td style={{ ...tdStyle, fontWeight: '600' }}>
                    {ticket.type}
                  </td>
                  <td style={tdStyle}>{ticket.ticketsSold.toLocaleString()}</td>
                  <td style={tdStyle}>{formatCurrency(ticket.appSales)}</td>
                  <td style={tdStyle}>{formatCurrency(ticket.posSales)}</td>
                  <td
                    style={{ ...tdStyle, fontWeight: '600', color: '#fa4616' }}
                  >
                    {formatCurrency(ticket.profit)}
                  </td>
                  <td
                    style={{ ...tdStyle, fontWeight: '600', color: '#fa4616' }}
                  >
                    {formatCurrency(ticket.avgProfit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Membership & Annual Sales */}
      <div style={cardStyle}>
        <h3
          style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '1.5rem',
          }}
        >
          Membership & Annual Sales
        </h3>

        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Membership Type</th>
                <th style={thStyle}>Active Members</th>
                <th style={thStyle}>New This Period</th>
                <th style={thStyle}>Renewals</th>
                <th style={thStyle}>App Sales</th>
                <th style={thStyle}>POS Sales</th>
                <th style={thStyle}>Our Profit</th>
                <th style={thStyle}>Avg Profit/Member</th>
              </tr>
            </thead>
            <tbody>
              {membershipTypes.map((membership, index) => (
                <tr key={index}>
                  <td style={{ ...tdStyle, fontWeight: '600' }}>
                    {membership.type}
                  </td>
                  <td style={tdStyle}>
                    {membership.activeMemberships.toLocaleString()}
                  </td>
                  <td style={tdStyle}>{membership.newThisPeriod}</td>
                  <td style={tdStyle}>{membership.renewalsThisPeriod}</td>
                  <td style={tdStyle}>{formatCurrency(membership.appSales)}</td>
                  <td style={tdStyle}>{formatCurrency(membership.posSales)}</td>
                  <td
                    style={{ ...tdStyle, fontWeight: '600', color: '#fa4616' }}
                  >
                    {formatCurrency(membership.profit)}
                  </td>
                  <td
                    style={{ ...tdStyle, fontWeight: '600', color: '#fa4616' }}
                  >
                    {formatCurrency(membership.avgProfit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
        }}
      >
        <div style={cardStyle}>
          <h3
            style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              marginBottom: '1.5rem',
            }}
          >
            App vs POS Performance
          </h3>
          <div style={{ marginBottom: '1rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem 0',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
              >
                <span
                  style={{
                    backgroundColor: '#fa4616',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                  }}
                >
                  APP
                </span>
                <span>66.4% of total profit</span>
              </div>
              <span style={{ fontWeight: '600', color: '#fa4616' }}>
                {formatCurrency(promoterData.appProfit)}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem 0',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
              >
                <span
                  style={{
                    backgroundColor: '#000033',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                  }}
                >
                  POS
                </span>
                <span>33.6% of total profit</span>
              </div>
              <span style={{ fontWeight: '600', color: '#fa4616' }}>
                {formatCurrency(promoterData.posProfit)}
              </span>
            </div>
          </div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
            App generates 2x more profit per dollar sold
          </p>
        </div>

        <div style={cardStyle}>
          <h3
            style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              marginBottom: '1.5rem',
            }}
          >
            Revenue Breakdown
          </h3>
          <div style={{ marginBottom: '1rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem 0',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
              >
                <span
                  style={{
                    backgroundColor: '#000033',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                  }}
                >
                  EVENTS
                </span>
                <span>Single event tickets</span>
              </div>
              <span style={{ fontWeight: '600', color: '#fa4616' }}>
                {formatCurrency(
                  ticketTypes.reduce((sum, ticket) => sum + ticket.profit, 0)
                )}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem 0',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
              >
                <span
                  style={{
                    backgroundColor: '#fa4616',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                  }}
                >
                  MEMBERSHIPS
                </span>
                <span>Annual & season passes</span>
              </div>
              <span style={{ fontWeight: '600', color: '#fa4616' }}>
                {formatCurrency(
                  membershipTypes.reduce(
                    (sum, membership) => sum + membership.profit,
                    0
                  )
                )}
              </span>
            </div>
          </div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
            Memberships provide recurring revenue stream
          </p>
        </div>
      </div>
    </div>
  );
}
